"use client";

import { useState, useEffect } from "react";

/**
 * Shared, screenshot-led walkthrough for connecting Anki via AnkiConnect.
 * Rendered both inside Settings (Anki Integration card) and on /help so the
 * setup instructions live in exactly one place.
 *
 * Why screenshots: a real user followed text-only steps, configured CORS
 * correctly, and still couldn't tell whether they'd done it right -- the
 * AnkiConnect config dialog is the step people get lost in. The navigation
 * steps are shown as real screenshots; the origins themselves are a
 * copy-paste block (you can't copy text out of an image).
 *
 * Images live in frontend/public/anki/ -- see the README there for the exact
 * shots each slot expects.
 */

// The WHOLE config object, not just the webCorsOriginList key.
//
// This used to be the bare `"webCorsOriginList": [...]` fragment, which is
// not valid on its own: the AnkiConnect config box holds one complete JSON
// object, so anyone who selected-all and pasted got a syntax error, and
// anyone who tried to merge it by hand was left editing JSON in a small
// textarea -- the exact step this guide exists to remove.
//
// Values below are AnkiConnect's own defaults (confirmed against two real
// users' config dialogs) with the two Maren origins appended. Keeping
// "http://localhost" preserves the default entry rather than silently
// dropping it, and keeping webBindAddress/webBindPort means a paste-over
// cannot move the port out from under lib/ankiConnect.ts.
const CORS_SNIPPET = `{
    "apiKey": null,
    "apiLogPath": null,
    "ignoreOriginList": [],
    "webBindAddress": "127.0.0.1",
    "webBindPort": 8765,
    "webCorsOriginList": [
        "http://localhost",
        "https://www.joinmaren.com",
        "https://joinmaren.com"
    ]
}`;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          /* clipboard blocked -- user can select manually */
        }
      }}
      className="text-xs font-semibold text-maren-green dark:text-maren-gold hover:opacity-80 transition"
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function StepImage({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <figure className="mt-3">
      <div className="overflow-hidden rounded-xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal shadow-sm">
        {/* Plain img (not next/image): these are user-captured screenshots of
            varying dimensions, so we let the browser size by intrinsic ratio
            rather than hard-coding width/height. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={src} alt={alt} loading="lazy" className="w-full h-auto block" />
      </div>
      <figcaption className="mt-2 text-xs text-maren-charcoal/55 dark:text-maren-stone/55 leading-relaxed">
        {caption}
      </figcaption>
    </figure>
  );
}

export default function AnkiSetupGuide() {
  // Safari (WebKit) blocks https pages from reaching http://127.0.0.1, so the
  // local AnkiConnect bridge can't work there -- no setting fixes it, and it's
  // not specific to Maren (every AnkiConnect web tool has the same limit).
  // Detect after mount to avoid an SSR/hydration mismatch (server has no UA).
  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    const ua = navigator.userAgent;
    setIsSafari(
      /safari/i.test(ua) &&
        !/chrome|chromium|crios|fxios|edg|android|opr|samsungbrowser/i.test(ua),
    );
  }, []);

  return (
    <div className="space-y-5">
      {isSafari && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-0.5">
            You&apos;re using Safari
          </p>
          <p className="text-xs text-amber-900/80 dark:text-amber-200/80 leading-relaxed">
            Safari can&apos;t connect to Anki &mdash; it blocks the local connection for security, and no
            setting changes that. Open <span className="font-medium">joinmaren.com</span> in{" "}
            <span className="font-medium">Chrome, Edge, or Firefox</span> and the steps below will work. This
            isn&apos;t specific to Maren; no website can reach Anki through Safari.
          </p>
        </div>
      )}
      <ol className="space-y-5">
        <li>
          <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">1.</span>{" "}
            Install <span className="font-medium">Anki desktop</span> (free, from ankiweb.net) and open it.
            Keep Anki running while you use Maren.
          </p>
        </li>

        <li>
          <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">2.</span>{" "}
            In Anki, open <span className="font-medium">Tools &rarr; Add-ons &rarr; Get Add-ons</span>, paste the
            code <code className="font-mono font-bold text-maren-green dark:text-maren-gold">2055492159</code>{" "}
            (AnkiConnect), click OK, then restart Anki.
          </p>
          <StepImage
            src="/anki/setup-addons-menu.png"
            alt="Anki's Tools menu open with Add-ons highlighted"
            caption="Anki &rarr; Tools &rarr; Add-ons"
          />
        </li>

        <li>
          <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">3.</span>{" "}
            Open <span className="font-medium">Tools &rarr; Add-ons</span> again, select{" "}
            <span className="font-medium">AnkiConnect</span> in the list, and click{" "}
            <span className="font-medium">Config</span>.
          </p>
          <StepImage
            src="/anki/setup-ankiconnect-config.png"
            alt="The Anki Add-ons window with AnkiConnect selected and the Config button"
            caption="Select AnkiConnect, then click Config"
          />
        </li>

        <li>
          <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">4.</span>{" "}
            Select everything already in the config box, delete it, paste this in its place,
            then click OK:
          </p>
          <div className="mt-2 rounded-lg border border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone dark:bg-maren-charcoal/70 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-maren-sage/30 dark:border-maren-sage/20">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-maren-charcoal/50 dark:text-maren-stone/55">
                AnkiConnect config
              </span>
              <CopyButton text={CORS_SNIPPET} />
            </div>
            <pre className="px-3 py-2.5 text-xs font-mono text-maren-charcoal/80 dark:text-maren-stone/80 overflow-x-auto whitespace-pre">{CORS_SNIPPET}</pre>
          </div>
          <p className="mt-2 text-xs text-maren-charcoal/55 dark:text-maren-stone/55 leading-relaxed">
            Those are AnkiConnect&apos;s normal defaults with the two Maren addresses added. If
            you have changed this config yourself before (an API key, a different port, other
            sites in the list), don&apos;t replace it. Add just the two{" "}
            <code className="font-mono bg-maren-sage/20 dark:bg-maren-charcoal px-1 rounded">joinmaren.com</code>{" "}
            lines to your existing{" "}
            <code className="font-mono bg-maren-sage/20 dark:bg-maren-charcoal px-1 rounded">webCorsOriginList</code>.
          </p>
        </li>

        <li>
          <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">5.</span>{" "}
            Restart Anki one more time. Then, back in Maren, go to{" "}
            <span className="font-medium">Settings → Maren Context → Anki Integration</span>{" "}
            and click <span className="font-medium">Test connection</span>. The status turns to{" "}
            <span className="font-medium">Connected</span> and your decks appear underneath it.
          </p>
        </li>

        <li>
          <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
            <span className="font-semibold text-maren-charcoal dark:text-maren-stone">6.</span>{" "}
            Tick the decks you actually study from, then click{" "}
            <span className="font-medium">Save</span>. Maren only reads the decks you pick, and
            your Anki card health won&apos;t show up on the Dashboard until at least one deck is
            saved.
          </p>
        </li>
      </ol>

      <p className="text-xs text-maren-charcoal/50 dark:text-maren-stone/55 leading-relaxed">
        Anki must be open on this computer for Maren to read your decks. Card health syncs only while Anki is
        running. You only do this setup once &mdash; the addresses stay saved.
      </p>
    </div>
  );
}
